import { Button, Spinner, Tabs, TabsContent, TabsList, TabsTrigger } from '@1trading/react';
import { Download } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { formatTermsRowForCsv, TERMS_CSV_HEADERS } from '@/features/terms/terms-columns';
import { TermsDownloadDialog, type TermsExportSelection } from '@/features/terms/terms-download-dialog';
import { applyTermsFilters, EMPTY_TERMS_FILTERS, type TermsFilterState } from '@/features/terms/terms-filter-bar';
import type { TermsSearchValue } from '@/features/terms/terms-search-controls';
import { TermsTabPanel } from '@/features/terms/terms-tab-panel';
import { useTermsAgreementSearch, useTermsAgreementsAll, useTermsProAll, useTermsProSearch, useTermsVersions } from '@/hooks/queries/compliance/use-terms';
import { useAsyncFileExport } from '@/hooks/use-async-file-export';
import { getErrorMessage } from '@/lib/api/error';
import { type TermsRow, USER_STATUS_VALUES } from '@/services/api/terms';
import { downloadCsv, toCsv } from '@/utils/csv';

type TermsTabId = 'agreements' | 'pro';

const NO_ROWS: TermsRow[] = [];
const NO_VERSIONS: string[] = [];
const NO_EXPORT_PARAMETERS: Record<string, unknown> = {};

// Deliberate fix: the legacy spinner hung forever when the WS push
// never arrived. After this grace period the spinner stops; the shared listener
// still auto-downloads the file whenever the export completes.
const EXPORT_SPINNER_TIMEOUT_MS = 10 * 60_000;

// Legacy fallback copy
const VERSIONS_ERROR_MESSAGE = 'An error occurred while loading terms versions';

const TermsAndConditionsPage = () => {
  const [activeTab, setActiveTab] = useState<TermsTabId>('agreements');
  // Lazy-activation latch: a tab starts its full background load the
  // first time it becomes active and keeps streaming after switching away.
  const [activatedTabs, setActivatedTabs] = useState<Record<TermsTabId, boolean>>({ agreements: true, pro: false });

  const [agreementsSearch, setAgreementsSearch] = useState<TermsSearchValue>({});
  const [proSearch, setProSearch] = useState<TermsSearchValue>({});
  const [agreementsFilters, setAgreementsFilters] = useState<TermsFilterState>(EMPTY_TERMS_FILTERS);
  const [proFilters, setProFilters] = useState<TermsFilterState>(EMPTY_TERMS_FILTERS);

  const isAgreementsSearchActive = Boolean(agreementsSearch.identifier);
  const isProSearchActive = Boolean(proSearch.identifier);

  // Full-dataset background pagination pauses while an identifier search is
  // active (the search result replaces the list) and resumes when
  // the search chip is cleared.
  const agreementsQuery = useTermsAgreementsAll(activatedTabs.agreements && !isAgreementsSearchActive, agreementsSearch.version);
  const proQuery = useTermsProAll(activatedTabs.pro && !isProSearchActive);
  const agreementsSearchQuery = useTermsAgreementSearch(agreementsSearch.identifier, agreementsSearch.version);
  const proSearchQuery = useTermsProSearch(proSearch.identifier);
  // Fired on mount — backs the download dialog's version filter
  const versionsQuery = useTermsVersions();

  const agreementsRows = (isAgreementsSearchActive ? agreementsSearchQuery.data : agreementsQuery.data) ?? NO_ROWS;
  const proRows = (isProSearchActive ? proSearchQuery.data : proQuery.data) ?? NO_ROWS;
  const agreementsFilteredRows = useMemo(() => applyTermsFilters(agreementsRows, agreementsFilters), [agreementsRows, agreementsFilters]);
  const proFilteredRows = useMemo(() => applyTermsFilters(proRows, proFilters), [proRows, proFilters]);

  const isAgreementsBackgroundLoading = !isAgreementsSearchActive && !agreementsQuery.isError && (agreementsQuery.hasNextPage || agreementsQuery.isFetchingNextPage);
  const isProBackgroundLoading = !isProSearchActive && !proQuery.isError && (proQuery.hasNextPage || proQuery.isFetchingNextPage);

  // Blocking load until the first page with data arrives; afterwards the table
  // stays interactive while the rest streams in
  const isAgreementsLoading = isAgreementsSearchActive
    ? agreementsSearchQuery.isPending
    : agreementsQuery.isPending || (agreementsRows.length === 0 && isAgreementsBackgroundLoading);
  const isProLoading = isProSearchActive ? proSearchQuery.isPending : proQuery.isPending || (proRows.length === 0 && isProBackgroundLoading);

  const agreementsError = isAgreementsSearchActive
    ? agreementsSearchQuery.isError
      ? getErrorMessage(agreementsSearchQuery.error)
      : null
    : agreementsQuery.isError
      ? getErrorMessage(agreementsQuery.error)
      : null;
  const proError = isProSearchActive ? (proSearchQuery.isError ? getErrorMessage(proSearchQuery.error) : null) : proQuery.isError ? getErrorMessage(proQuery.error) : null;

  const retryAgreements = () => {
    if (isAgreementsSearchActive) {
      void agreementsSearchQuery.refetch();
      return;
    }
    if (agreementsQuery.data && agreementsQuery.hasNextPage) {
      void agreementsQuery.fetchNextPage();
      return;
    }
    void agreementsQuery.refetch();
  };

  const retryPro = () => {
    if (isProSearchActive) {
      void proSearchQuery.refetch();
      return;
    }
    if (proQuery.data && proQuery.hasNextPage) {
      void proQuery.fetchNextPage();
      return;
    }
    void proQuery.refetch();
  };

  const handleTabChange = (value: string) => {
    const tab = value as TermsTabId;
    setActiveTab(tab);
    setActivatedTabs((previous) => (previous[tab] ? previous : { ...previous, [tab]: true }));
  };

  const handleAgreementsSearch = (value: TermsSearchValue) => {
    if (value.identifier === agreementsSearch.identifier && value.version === agreementsSearch.version) {
      if (value.identifier) void agreementsSearchQuery.refetch();
      return;
    }
    setAgreementsSearch(value);
  };

  const handleProSearch = (value: TermsSearchValue) => {
    if (value.identifier === proSearch.identifier) {
      if (value.identifier) void proSearchQuery.refetch();
      return;
    }
    setProSearch({ identifier: value.identifier });
  };

  // --- Downloads ---------------------------------------------------------

  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  // Confirming the dialog computes the export parameters from the dialog's
  // selection argument, which only exists once the dialog closes — so the
  // request fires from the effect below, once the hook has re-rendered with
  // the new parameters, rather than synchronously in the confirm handler.
  const [pendingExportParameters, setPendingExportParameters] = useState<Record<string, unknown> | null>(null);

  const { isExporting, requestExport } = useAsyncFileExport('terms-agreements', pendingExportParameters ?? NO_EXPORT_PARAMETERS, {
    timeoutMs: EXPORT_SPINNER_TIMEOUT_MS,
  });

  useEffect(() => {
    if (!pendingExportParameters) return;
    setPendingExportParameters(null);
    void requestExport();
  }, [pendingExportParameters, requestExport]);

  // NOTE: the server-generated CSV's `Owner ID` column contains the
  // email (upstream processFileDownload bug). The client cannot fix it — do not
  // "correct" the UI to match.
  const handleExportConfirm = ({ statuses, versions }: TermsExportSelection) => {
    setIsDownloadDialogOpen(false);
    // Selecting every status means "no status filter" so users with an unknown
    // status are still included — status is derived server-side and can be
    // 'unknown'; sending all five explicitly would EXCLUDE those users
    // (load-bearing).
    const statusesFilter = statuses.length === USER_STATUS_VALUES.length ? undefined : statuses;
    const versionsFilter = versions.length > 0 ? versions : undefined;
    setPendingExportParameters({
      ...(statusesFilter ? { statuses: statusesFilter } : {}),
      ...(versionsFilter ? { versions: versionsFilter } : {}),
    });
  };

  // Legacy tab: client-side CSV of the table's currently FILTERED rows, raw
  // values under the column titles
  const handleDownloadProCsv = () => {
    const csv = toCsv(proFilteredRows.map(formatTermsRowForCsv), [...TERMS_CSV_HEADERS]);
    downloadCsv(csv, `pro-terms-${new Date().toISOString()}.csv`);
  };

  // The button's action and loading state bind to the active tab
  const isDownloadBusy = activeTab === 'agreements' && isExporting;

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <h1 className="text-2xl font-semibold">Terms &amp; Conditions</h1>

      <Tabs onValueChange={handleTabChange} value={activeTab}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="agreements">Terms Agreements</TabsTrigger>
            <TabsTrigger value="pro">Pro Terms (Legacy)</TabsTrigger>
          </TabsList>
          <Button className="gap-2" disabled={isDownloadBusy} onClick={activeTab === 'agreements' ? () => setIsDownloadDialogOpen(true) : handleDownloadProCsv} type="button">
            {isDownloadBusy ? <Spinner className="size-4" /> : <Download className="size-4" />}
            Download Data
          </Button>
        </div>

        <TabsContent className="mt-4" value="agreements">
          <TermsTabPanel
            errorMessage={agreementsError}
            filteredRows={agreementsFilteredRows}
            filters={agreementsFilters}
            hasVersionSearch
            idPrefix="terms-agreements"
            isBackgroundLoading={isAgreementsBackgroundLoading}
            isLoading={isAgreementsLoading}
            isSearching={agreementsSearchQuery.isFetching}
            onFiltersChange={setAgreementsFilters}
            onRetry={retryAgreements}
            onSearch={handleAgreementsSearch}
            rows={agreementsRows}
            search={agreementsSearch}
          />
        </TabsContent>
        <TabsContent className="mt-4" value="pro">
          <TermsTabPanel
            errorMessage={proError}
            filteredRows={proFilteredRows}
            filters={proFilters}
            hasVersionSearch={false}
            idPrefix="terms-pro"
            isBackgroundLoading={isProBackgroundLoading}
            isLoading={isProLoading}
            isSearching={proSearchQuery.isFetching}
            onFiltersChange={setProFilters}
            onRetry={retryPro}
            onSearch={handleProSearch}
            rows={proRows}
            search={proSearch}
          />
        </TabsContent>
      </Tabs>

      <TermsDownloadDialog
        isDownloading={isExporting}
        isVersionsLoading={versionsQuery.isPending}
        onConfirm={handleExportConfirm}
        onOpenChange={setIsDownloadDialogOpen}
        onRetryVersions={() => void versionsQuery.refetch()}
        open={isDownloadDialogOpen}
        versions={versionsQuery.data ?? NO_VERSIONS}
        versionsError={versionsQuery.isError ? VERSIONS_ERROR_MESSAGE : null}
      />
    </div>
  );
};
TermsAndConditionsPage.displayName = 'TermsAndConditionsPage';

export default TermsAndConditionsPage;
